import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatExactDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Same rules as api/src/dbRoutes.js sanitizeName + buildDbName (prefix + sanitized segment). */
function previewDatabaseName(raw, dbPrefix) {
  const s = String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (!s) return '';
  return `${dbPrefix}${s}`;
}

function DbCardSkeleton() {
  return (
    <div className="db-card db-card-skeleton">
      <div className="skeleton-line skeleton-title" />
      <div className="skeleton-line skeleton-meta" />
      <div className="skeleton-actions" />
    </div>
  );
}

export default function Databases() {
  const [dbs, setDbs] = useState([]);
  const [dbPrefix, setDbPrefix] = useState('erp-v1.0-');
  const [protectedDbs, setProtectedDbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dropping, setDropping] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '',
    createMode: 'copy',
    source: 'development',
  });
  const [toast, setToast] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null);
  const [search, setSearch] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get('/config');
      if (r.data.dbPrefix) setDbPrefix(r.data.dbPrefix);
      if (Array.isArray(r.data.protectedDbs)) setProtectedDbs(r.data.protectedDbs);
    } catch {
      /* keep default */
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/databases');
      setDbs(r.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load databases', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const body = {
        name: form.name.trim(),
        createMode: form.createMode,
        source: form.createMode === 'copy' ? form.source : undefined,
      };
      const r = await api.post('/databases', body);
      showToast(r.data.message);
      setShowCreate(false);
      setForm({
        name: '',
        createMode: 'copy',
        source: 'development',
      });
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create database', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function confirmDrop() {
    const dbName = pendingDrop;
    if (!dbName) return;
    setDropping(dbName);
    try {
      const r = await api.delete(`/databases/${encodeURIComponent(dbName)}`);
      showToast(r.data.message);
      setPendingDrop(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Drop failed', 'error');
    } finally {
      setDropping(null);
    }
  }

  const { protectedRows, droppableRows } = useMemo(() => {
    const q = search.toLowerCase();
    const f = dbs.filter((d) => d.name.toLowerCase().includes(q));
    const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    return {
      protectedRows: f.filter((d) => !d.canDrop).sort(byName),
      droppableRows: f.filter((d) => d.canDrop).sort(byName),
    };
  }, [dbs, search]);

  const listEmpty = protectedRows.length === 0 && droppableRows.length === 0;

  const showBranchEmptyNotice =
    !loading && !listEmpty && droppableRows.length === 0 && protectedRows.length > 0;

  const hasBranchSection = droppableRows.length > 0 || showBranchEmptyNotice;
  /** Subheading under the main list only when protected cards appear above the branch area */
  const showBranchSubsectionHeader = protectedRows.length > 0 && hasBranchSection;

  const branchNamePreview = previewDatabaseName(form.name, dbPrefix);

  function renderDbCard(db) {
    const online = db.state != null;
    return (
      <article key={db.name} className="db-card db-card-glass">
        <div className="db-card-header">
          <span
            className={`db-status-dot ${online ? 'db-status-online' : 'db-status-offline'}`}
            title={online ? 'On server' : 'Unknown'}
          />
          <div>
            <span className="db-name">{db.name}</span>
            <div className="db-detail-line" title="Created (server time)">
              {formatExactDate(db.createDate)}
            </div>
          </div>
        </div>
        <div className="db-meta">
          <span className="db-meta-item" title="Relative time">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
              <path d="M6 3.5V6l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            {timeAgo(db.createDate)}
          </span>
          <span className="db-meta-item" title="Approximate size on disk">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            </svg>
            {db.sizeMB != null ? `${db.sizeMB} MB` : '—'}
          </span>
          <span
            className={`db-state-pill ${online ? `db-state-${String(db.state).toLowerCase()}` : 'db-state-unknown'}`}
          >
            {online ? db.state : '—'}
          </span>
        </div>
        <div className="db-actions">
          {db.canDrop ? (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => setPendingDrop(db.name)}
              disabled={dropping === db.name}
            >
              {dropping === db.name ? 'Dropping…' : 'Drop'}
            </button>
          ) : (
            <span className="db-no-drop" title="Listed in PROTECTED_DBS — cannot be dropped from this tool">
              Protected
            </span>
          )}
        </div>
      </article>
    );
  }

  return (
    <div className="page page-databases">
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      {protectedDbs.length > 0 && (
        <section className="protected-section" aria-labelledby="protected-heading">
          <h3 id="protected-heading">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            Protected databases
          </h3>
          <p className="page-sub" style={{ marginBottom: '12px', marginTop: '-4px' }}>
            These names cannot be dropped from this tool (system + sources). New branch DBs use prefix{' '}
            <code className="inline-code">{dbPrefix}</code>.
          </p>
          <div className="protected-list">
            {protectedDbs.map((name) => (
              <span
                key={name}
                className="protected-tag"
                title="Cannot be dropped via DB Manager"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                {name}
              </span>
            ))}
          </div>
        </section>
      )}

      <div className="page-header">
        <div>
          <h2>Databases</h2>
          <p className="page-sub">
            {dbs.length} database{dbs.length !== 1 ? 's' : ''} on this server.
            {' '}
            Drop is available for any database except those listed in Protected above.
          </p>
        </div>
        <div className="header-actions">
          <input
            className="search-input"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search databases"
          />
          <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New database
          </button>
        </div>
      </div>

      {pendingDrop && (
        <div
          className="modal-overlay"
          role="presentation"
          onClick={() => {
            if (!dropping) setPendingDrop(null);
          }}
        >
          <div
            className="modal modal-glass"
            role="dialog"
            aria-labelledby="drop-title"
            aria-describedby="drop-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="drop-title">Drop database?</h3>
            <p id="drop-desc" className="modal-sub">
              This will permanently delete{' '}
              <code className="mono-inline">{pendingDrop}</code>. This cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPendingDrop(null)}
                disabled={dropping === pendingDrop}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDrop}
                disabled={dropping === pendingDrop}
              >
                {dropping === pendingDrop ? 'Dropping…' : 'Drop database'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowCreate(false)}>
          <div className="modal modal-glass" role="dialog" aria-labelledby="create-title" onClick={(e) => e.stopPropagation()}>
            <h3 id="create-title">New database</h3>
            <p className="modal-sub">Blank for migrations, or a full copy from Development or Staging.</p>
            <form onSubmit={handleCreate}>
              <div className="field">
                <label htmlFor="branch-name">Branch / name</label>
                <input
                  id="branch-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. feature-auth"
                  required
                  autoFocus
                  aria-describedby={branchNamePreview ? 'branch-name-preview' : undefined}
                />
                {branchNamePreview ? (
                  <p id="branch-name-preview" className="field-hint field-hint-preview">
                    Database name:{' '}
                    <code className="mono-inline">{branchNamePreview}</code>
                  </p>
                ) : null}
              </div>

              <div className="field">
                <span className="field-label-block">Type</span>
                <label className="radio-row">
                  <input
                    type="radio"
                    name="createMode"
                    checked={form.createMode === 'copy'}
                    onChange={() => setForm((f) => ({ ...f, createMode: 'copy' }))}
                  />
                  <span>
                    <strong>Copy</strong> — full backup/restore from a source (schema + data)
                  </span>
                </label>
                <label className="radio-row">
                  <input
                    type="radio"
                    name="createMode"
                    checked={form.createMode === 'blank'}
                    onChange={() => setForm((f) => ({ ...f, createMode: 'blank' }))}
                  />
                  <span>
                    <strong>Blank</strong> — empty database for migrations
                  </span>
                </label>
              </div>

              {form.createMode === 'copy' && (
                <div className="field">
                  <label htmlFor="clone-source">Copy from</label>
                  <select
                    id="clone-source"
                    value={form.source}
                    onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                  >
                    <option value="development">Development</option>
                    <option value="staging">Staging</option>
                  </select>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="db-grid">
          <DbCardSkeleton />
          <DbCardSkeleton />
          <DbCardSkeleton />
        </div>
      ) : listEmpty ? (
        <div className="empty-state empty-state-modern">
          <div className="empty-icon-wrap">
            <svg width="44" height="44" viewBox="0 0 48 48" fill="none" aria-hidden>
              <ellipse cx="24" cy="16" rx="16" ry="7" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 16v8c0 3.87 7.16 7 16 7s16-3.13 16-7v-8" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 24v8c0 3.87 7.16 7 16 7s16-3.13 16-7v-8" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
            </svg>
          </div>
          <p className="empty-title">{search ? 'No matches' : 'No databases'}</p>
          <p className="empty-desc">
            {search ? 'Try another search term' : 'Create a branch database or check SQL Server.'}
          </p>
          {!search && (
            <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
              Create database
            </button>
          )}
        </div>
      ) : (
        <div className="db-grid-stack">
          {protectedRows.length > 0 && (
            <div className="db-grid">{protectedRows.map(renderDbCard)}</div>
          )}
          {protectedRows.length > 0 && hasBranchSection && (
            <div className="db-grid-divider" role="separator" aria-hidden="true" />
          )}
          {showBranchSubsectionHeader && (
            <div className="db-branch-section">
              <h3 className="db-branch-section-title">Branch databases</h3>
            </div>
          )}
          {showBranchEmptyNotice && (
            <div className="db-branch-empty-notice db-branch-empty-notice-branch-slot" role="status">
              <p>
                No branch-based database exists{search ? ' matching your search' : ''}. Only protected instances appear
                in the cards above. Use <strong>+ New database</strong> to create one.
              </p>
            </div>
          )}
          {droppableRows.length > 0 && (
            <div className="db-grid db-grid-branch">{droppableRows.map(renderDbCard)}</div>
          )}
        </div>
      )}
    </div>
  );
}
